import { useEffect, useMemo, useState } from "react";
import {
    Button, Card, Col, Empty, Input, Modal, Row, Select, Spin, Table, Tag, Typography, message,
} from "antd";
import {
    SafetyCertificateOutlined, CheckCircleOutlined, CloseCircleOutlined, EyeOutlined,
    FileImageOutlined, FilePdfOutlined, BankOutlined,
} from "@ant-design/icons";
import SideBar from "../../components/SideBar";
import NavBar from "../../components/NavBar";
import Footer from "../../components/Footer";
import axiosInstance from "../../variables/api/axiosInstance";
import { displayNumbers } from "../../variables/numbersFunctions";
import { frenchDate } from "../../variables/functions";
import { LOGGED_USER } from "../../variables/constants";

const { Text, Title } = Typography;
const { TextArea } = Input;
const PRIMARY = "#242166";

const STATUT = {
    EN_ATTENTE: { label: "À valider", color: "gold" },
    VALIDE: { label: "Validé", color: "green" },
    REJETE: { label: "Rejeté", color: "red" },
};

// Pièces attendues (ordre d'affichage).
const PIECES = [
    { type: "CHEQUE", label: "Chèque", presence: "chequeDepose", ct: "contentTypeCheque", nom: "nomOrigCheque" },
    { type: "PIECE_IDENTITE", label: "Pièce d'identité", presence: "pieceDeposee", ct: "contentTypePiece", nom: "nomOrigPiece" },
    { type: "QUITTANCE", label: "Quittance signée", presence: "quittanceDeposee", ct: "contentTypeQuittance", nom: "nomOrigQuittance" },
];

/**
 * Écran POOL de vérification des justificatifs de paiement déposés depuis le mobile.
 * Visualise chèque / pièce d'identité / quittance de chaque règlement puis valide ou rejette.
 * Réservé au Pool (ou habilitation HJP001) — le backend refait la garde (403).
 */
const VerificationJustificatifsPool = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [organismes, setOrganismes] = useState([]);
    const [orgFilter, setOrgFilter] = useState(null);

    const [reviewing, setReviewing] = useState(null); // justificatif en revue (modal)
    const [docUrls, setDocUrls] = useState({});        // { CHEQUE: blobUrl, ... }
    const [docLoading, setDocLoading] = useState(false);
    const [acting, setActing] = useState(false);

    const [rejectOpen, setRejectOpen] = useState(false);
    const [motif, setMotif] = useState("");

    const canVerify = useMemo(() => {
        const org = LOGGED_USER?.profil?.organisme;
        const isPool = org?.typeOrganisme === "POOL" || org?.code === "00";
        const hasCode = (LOGGED_USER?.profil?.habilitations || []).some((h) => h?.codeHabilitation === "HJP001");
        return isPool || hasCode;
    }, []);

    useEffect(() => {
        axiosInstance.get("/api/organisme/all/assurances-pool")
            .then((res) => setOrganismes(Array.isArray(res.data) ? res.data : []))
            .catch(() => { });
    }, []);

    const fetchRows = () => {
        setLoading(true);
        axiosInstance.get("/api/justificatif-paiement/a-valider", { params: { organismeCode: orgFilter || undefined } })
            .then((res) => setRows(Array.isArray(res.data) ? res.data : []))
            .catch(() => message.error("Erreur lors du chargement des justificatifs"))
            .finally(() => setLoading(false));
    };
    useEffect(() => { fetchRows(); }, [orgFilter]); // eslint-disable-line

    // Charge les pièces présentes d'un justificatif comme blobs (auth via axiosInstance).
    const openReview = async (r) => {
        setReviewing(r);
        setDocUrls({});
        setDocLoading(true);
        const urls = {};
        for (const p of PIECES) {
            if (!r[p.presence]) continue;
            try {
                const res = await axiosInstance.get(`/api/justificatif-paiement/${r.codeId}/fichier/${p.type}`,
                    { responseType: "blob" });
                urls[p.type] = { url: URL.createObjectURL(res.data), ct: r[p.ct] || res.data.type };
            } catch (e) { /* pièce illisible → ignorée */ }
        }
        setDocUrls(urls);
        setDocLoading(false);
    };

    const closeReview = () => {
        Object.values(docUrls).forEach((d) => d?.url && URL.revokeObjectURL(d.url));
        setDocUrls({});
        setReviewing(null);
        setRejectOpen(false);
        setMotif("");
    };

    const doValider = async () => {
        if (!reviewing) return;
        setActing(true);
        try {
            await axiosInstance.post(`/api/justificatif-paiement/${reviewing.codeId}/valider`);
            message.success("Justificatifs validés");
            closeReview(); fetchRows();
        } catch (e) {
            message.error(e?.response?.status === 403 ? "Droit insuffisant (Pool requis)" : "Échec de la validation");
        } finally { setActing(false); }
    };

    const doRejeter = async () => {
        if (!reviewing) return;
        if (!motif.trim()) { message.warning("Le motif de rejet est obligatoire"); return; }
        setActing(true);
        try {
            await axiosInstance.post(`/api/justificatif-paiement/${reviewing.codeId}/rejeter`, { motif: motif.trim() });
            message.success("Justificatifs rejetés — la compagnie est notifiée");
            closeReview(); fetchRows();
        } catch (e) {
            message.error(e?.response?.status === 403 ? "Droit insuffisant (Pool requis)" : "Échec du rejet");
        } finally { setActing(false); }
    };

    const columns = [
        {
            title: "N° sinistre / règlement", key: "id", render: (_, r) => (
                <div>
                    <div style={{ fontWeight: 600, color: "#111827" }}>{r.numSinistre || "—"}</div>
                    <div style={{ fontSize: 12, color: "#6b7280" }}>Règlement {r.numeroReglement || "—"}</div>
                </div>
            ),
        },
        { title: "Compagnie", dataIndex: "organismeNom", key: "org", render: (v, r) => v || r.organismeCode || "—" },
        { title: "Bénéficiaire", dataIndex: "beneficiaire", key: "benef", render: (v) => v || "—" },
        { title: "Montant", dataIndex: "montant", key: "montant", align: "right", render: (v) => <strong style={{ color: PRIMARY }}>{displayNumbers(v || 0)} F</strong> },
        {
            title: "Pièces", key: "pieces", align: "center", render: (_, r) => (
                <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
                    {PIECES.map((p) => (
                        <Tag key={p.type} color={r[p.presence] ? "blue" : "default"} style={{ margin: 0, borderRadius: 6 }}>
                            {p.label.split(" ")[0]}
                        </Tag>
                    ))}
                </div>
            ),
        },
        { title: "Déposé le", dataIndex: "dateDepot", key: "date", render: (v) => (v ? frenchDate(v) : "—") },
        {
            title: "", key: "action", align: "center", render: (_, r) => (
                <Button type="primary" icon={<EyeOutlined />} style={{ background: PRIMARY, borderColor: PRIMARY }}
                    onClick={() => openReview(r)}>Vérifier</Button>
            ),
        },
    ];

    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar />
                <div className="layout-page">
                    <NavBar />
                    <div className="content-wrapper" style={{ background: "#f0f2f5" }}>
                        <div className="container-xxl flex-grow-1 container-p-y">
                            <div style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, #3b3a86 100%)`, borderRadius: 16, padding: "20px 24px", marginBottom: 16, color: "#fff" }}>
                                <Title level={3} style={{ color: "#fff", margin: 0 }}>
                                    <SafetyCertificateOutlined /> Vérification des justificatifs de paiement
                                </Title>
                                <Text style={{ color: "rgba(255,255,255,0.85)" }}>
                                    Contrôle des chèques, pièces d'identité et quittances déposés depuis le mobile. Seuls les règlements validés seront cédés.
                                </Text>
                            </div>

                            {!canVerify && (
                                <Card style={{ borderRadius: 12, marginBottom: 14 }}>
                                    <Text type="warning">Accès réservé au Pool (habilitation HJP001).</Text>
                                </Card>
                            )}

                            <Card bordered={false} style={{ borderRadius: 12, marginBottom: 14 }} bodyStyle={{ padding: 12 }}>
                                <Row gutter={[12, 12]} align="middle">
                                    <Col xs={24} md={10}>
                                        <Text type="secondary"><BankOutlined /> Compagnie</Text>
                                        <Select style={{ width: "100%", marginTop: 4 }} allowClear showSearch optionFilterProp="label"
                                            placeholder="Toutes les compagnies" value={orgFilter || undefined}
                                            onChange={(v) => setOrgFilter(v || null)}
                                            options={organismes.map((o) => ({ value: o.code, label: o.raisonSociale || o.nom || o.code }))} />
                                    </Col>
                                    <Col xs={24} md={14} style={{ textAlign: "right" }}>
                                        <Text type="secondary">{rows.length} règlement(s) à vérifier</Text>
                                    </Col>
                                </Row>
                            </Card>

                            <Card bordered={false} style={{ borderRadius: 12 }}>
                                <Table rowKey="codeId" columns={columns} dataSource={rows} loading={loading}
                                    pagination={{ pageSize: 10, showSizeChanger: true }}
                                    locale={{ emptyText: <Empty description="Aucun justificatif en attente" /> }} />
                            </Card>
                        </div>
                        <Footer />
                    </div>
                </div>
            </div>

            {/* Modal de revue des pièces */}
            <Modal open={!!reviewing} onCancel={closeReview} width={920} footer={null}
                title={reviewing ? `Justificatifs — règlement ${reviewing.numeroReglement || ""} · ${reviewing.numSinistre || ""}` : ""}>
                {reviewing && (
                    <div>
                        <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
                            <Col span={8}><Text type="secondary">Compagnie</Text><div>{reviewing.organismeNom || reviewing.organismeCode || "—"}</div></Col>
                            <Col span={8}><Text type="secondary">Bénéficiaire</Text><div>{reviewing.beneficiaire || "—"}</div></Col>
                            <Col span={8}><Text type="secondary">Montant</Text><div><strong>{displayNumbers(reviewing.montant || 0)} F</strong></div></Col>
                        </Row>
                        {reviewing.statutValidation && (
                            <Tag color={STATUT[reviewing.statutValidation]?.color} style={{ marginBottom: 12 }}>
                                {STATUT[reviewing.statutValidation]?.label || reviewing.statutValidation}
                            </Tag>
                        )}

                        {docLoading ? (
                            <div style={{ textAlign: "center", padding: 40 }}><Spin /></div>
                        ) : (
                            <Row gutter={[12, 12]}>
                                {PIECES.map((p) => {
                                    const present = reviewing[p.presence];
                                    const doc = docUrls[p.type];
                                    const isPdf = (doc?.ct || reviewing[p.ct] || "").includes("pdf");
                                    return (
                                        <Col xs={24} md={8} key={p.type}>
                                            <Card size="small" title={<span>{isPdf ? <FilePdfOutlined /> : <FileImageOutlined />} {p.label}</span>}
                                                bodyStyle={{ padding: 8, minHeight: 220, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                {!present ? (
                                                    <Text type="secondary">Non déposée</Text>
                                                ) : !doc ? (
                                                    <Text type="danger">Illisible</Text>
                                                ) : isPdf ? (
                                                    <iframe title={p.type} src={doc.url} style={{ width: "100%", height: 220, border: "none" }} />
                                                ) : (
                                                    <img alt={p.type} src={doc.url} style={{ maxWidth: "100%", maxHeight: 300, cursor: "zoom-in" }}
                                                        onClick={() => window.open(doc.url, "_blank")} />
                                                )}
                                            </Card>
                                        </Col>
                                    );
                                })}
                            </Row>
                        )}

                        {canVerify && (
                            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
                                <Button danger icon={<CloseCircleOutlined />} loading={acting} onClick={() => setRejectOpen(true)}>Rejeter</Button>
                                <Button type="primary" icon={<CheckCircleOutlined />} loading={acting}
                                    style={{ background: "#16a34a", borderColor: "#16a34a" }} onClick={doValider}>Valider</Button>
                            </div>
                        )}
                    </div>
                )}
            </Modal>

            {/* Modal motif de rejet */}
            <Modal open={rejectOpen} onCancel={() => setRejectOpen(false)} onOk={doRejeter} okText="Rejeter"
                okButtonProps={{ danger: true, loading: acting }} cancelText="Annuler" title="Motif du rejet">
                <Text type="secondary">La compagnie recevra une notification in-app et un e-mail pour rescanner.</Text>
                <TextArea rows={4} value={motif} onChange={(e) => setMotif(e.target.value)}
                    placeholder="Ex. chèque illisible, pièce d'identité expirée, quittance non signée…" style={{ marginTop: 8 }} />
            </Modal>
        </div>
    );
};

export default VerificationJustificatifsPool;
